import { CATEGORY_LABEL, CHANNEL_LABEL, CH_LABEL, PRIORITY_LABEL, STATUS_LABEL } from "../constants.ts";
import { inquirySourceBadge } from "../utils/inquirySource.ts";
import { slaBadgeClass, slaState } from "../utils/sla.ts";
import { ticketDisplayContext, ticketDisplayTitle } from "../utils/ticketDisplay.ts";
import type { Ticket } from "../types.ts";

/** 一覧の 1 行。SLA は残り/超過を数値で出し（改善 D-1）、
 *  担当は割当済み/未割当を必ず表示して 1 クリック割当を出す（改善 E）。 */
export function TicketCard({
  t,
  isSelected,
  canWrite,
  checked,
  showChannel,
  showAssignSelf,
  now,
  onToggleCheck,
  onOpen,
  onAssignSelf,
}: {
  t: Ticket;
  isSelected: boolean;
  canWrite: boolean;
  checked: boolean;
  /** チャネルを 1 つに絞っていないときだけ区分バッジを出す */
  showChannel: boolean;
  showAssignSelf: boolean;
  now: number;
  onToggleCheck: (checked: boolean) => void;
  onOpen: () => void;
  onAssignSelf: () => void;
}) {
  const sla = slaState(t, now);
  const context = ticketDisplayContext(t);

  return (
    <div className={canWrite ? "support-ticket-row" : "support-ticket-row support-ticket-row--no-select"}>
      {canWrite && (
        <label title="一括操作の選択" className="support-ticket-row__select">
          <input type="checkbox" checked={checked} onChange={(e) => onToggleCheck(e.target.checked)} />
        </label>
      )}
      <button
        type="button"
        className={
          "support-ticket-card"
          + (isSelected ? " support-ticket-card--selected" : "")
          + (t.priority === "urgent" ? " support-ticket-card--urgent" : "")
        }
        onClick={onOpen}
      >
        <div className="support-ticket-card__meta-row">
          <span className="support-ticket-card__number">
            {t.priority === "urgent" && (
              <span className="support-ticket-card__badge support-ticket-card__badge--urgent">
                {PRIORITY_LABEL.urgent}
              </span>
            )}
            <span className="support-mono-id">{t.ticket_number}</span>
            {sla.level !== "none" && (
              <span
                className={slaBadgeClass(sla)}
                title={t.sla_due_at ? `初回応答の期限: ${new Date(t.sla_due_at).toLocaleString()}` : ""}
              >
                {sla.label}
              </span>
            )}
            {!t.assigned_to ? (
              <span className="support-ticket-card__badge support-ticket-card__badge--unassigned">未割当</span>
            ) : (
              <span className="support-ticket-card__badge" title={`担当: ${t.assigned_to}`}>
                担当 {t.assigned_to.split("@")[0]}
              </span>
            )}
            {inquirySourceBadge(t.inquiry_source) && (
              <span className="support-ticket-card__badge" title={`起票経路: ${t.inquiry_source}`}>
                {inquirySourceBadge(t.inquiry_source)}
              </span>
            )}
            {t.source_alert_type && (
              <span className="support-ticket-card__badge" title={`自動起票: ${t.source_alert_type}`}>自動</span>
            )}
            {(t.sentiment === "angry" || t.sentiment === "negative") && (
              <span className="support-ticket-card__badge" title={`感情: ${t.sentiment}`}>
                {t.sentiment === "angry" ? "強い不満" : "不満"}
              </span>
            )}
            {(t.tags || []).slice(0, 2).map((tg) => (
              <span key={tg} className="support-ticket-card__badge">#{tg}</span>
            ))}
            {(t.tags || []).length > 2 && (
              <span className="support-ticket-card__badge" title={(t.tags || []).join(", ")}>
                +{(t.tags || []).length - 2}
              </span>
            )}
            {(t.open_task_count ?? 0) > 0 && (
              <span className="support-ticket-card__badge" title="未完了タスク">タスク {t.open_task_count}</span>
            )}
            {showChannel && (
              <span className="support-ticket-card__badge">{CHANNEL_LABEL[t.channel] || t.channel}</span>
            )}
          </span>
          <span className="support-ticket-card__status">{STATUS_LABEL[t.status] || t.status}</span>
        </div>
        <div className="support-ticket-card__title">{ticketDisplayTitle(t)}</div>
        {context && <div className="support-ticket-card__subtitle">{context}</div>}
        <div className="support-ticket-card__tags">
          {t.category && (
            <span className="support-ticket-card__tag" style={{ background: "var(--primary-light)", color: "var(--primary)" }}>
              {CATEGORY_LABEL[t.category] || t.category}
            </span>
          )}
          {(t.channels || []).map((ch) => (
            <span key={ch} className="support-ticket-card__tag" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              {CH_LABEL[ch] || ch}
            </span>
          ))}
        </div>
        {t.contact_email && (
          <div className="support-ticket-card__contact">
            {t.contact_name ? `${t.contact_name} ` : ""}&lt;{t.contact_email}&gt;
          </div>
        )}
      </button>
      {showAssignSelf && (
        <button
          type="button"
          className="support-ticket-row__assign"
          title="自分を担当にして対応を開始（顧客への通知は送りません）"
          onClick={(e) => {
            e.stopPropagation();
            onAssignSelf();
          }}
        >
          自分が対応
        </button>
      )}
    </div>
  );
}
